// Signal Engine — web modal placements (step b).
//   <FeedbackAutoModal/>  : app-open popup. Pulls an eligible `web_modal` campaign;
//                           if one exists, shows it as a centered modal. Renders
//                           nothing otherwise. Mount once (e.g. on the dashboard).
//   <HelpShapeLomiItem/>  : a menu row ("🍋 Help shape Lomi") that opens an on-demand
//                           modal — serves a `web_menu` campaign if any, else an
//                           open-ended ask. Always available (the "net").
// Both reuse <FeedbackCapture/>. See docs/feedback_engine_design.md.

import React, { useEffect, useRef, useState } from 'react';
import feedbackAPI, { ActiveResponse, SignalCampaign } from '../../services/feedbackAPI';
import FeedbackCapture from './FeedbackCapture';
import { useTheme } from '../../contexts/ThemeContext';

const OPEN_ENDED: SignalCampaign = {
  id: '', // empty → server treats campaign_id as null (open-ended)
  key: 'web_menu_open',
  name: 'Feedback',
  campaign_type: 'feedback',
  question_en: "What's on your mind? Tell us what's working and what's not — by voice or text.",
  question_am: 'ሐሳብዎ ምንድን ነው? የሚሰራውንና የማይሰራውን — በድምፅ ወይም በጽሑፍ ይንገሩን።',
  fields: null,
  modalities: ['text', 'voice'],
  reward_type: 'none',
};

const ModalShell: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: dark ? 'rgba(0,0,0,0.6)' : 'rgba(15,23,42,0.5)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: dark ? '#171b21' : '#fff', borderRadius: 16, maxWidth: 460, width: '100%',
          color: dark ? '#e6e8eb' : undefined,
          padding: '22px 20px', boxShadow: dark ? '0 16px 48px rgba(0,0,0,0.5)' : '0 16px 48px rgba(0,0,0,0.28)', position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ position: 'absolute', top: 10, right: 14, border: 'none', background: 'none', fontSize: 22, color: dark ? '#7d8590' : '#94a3b8', cursor: 'pointer', lineHeight: 1 }}
        >
          ×
        </button>
        {children}
      </div>
    </div>
  );
};

export const FeedbackAutoModal: React.FC<{ surface?: string; trigger?: string }> = ({
  surface = 'web_modal',
  trigger = 'app_open',
}) => {
  const [active, setActive] = useState<ActiveResponse | null>(null);
  const [closed, setClosed] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    feedbackAPI
      .getActive(surface, trigger)
      .then((res) => {
        if (res.campaign) {
          setActive(res);
          if (res.request_id) feedbackAPI.markOpen(res.request_id);
        }
      })
      .catch(() => {});
  }, [surface, trigger]);

  if (!active || !active.campaign || closed) return null;
  const dismiss = () => {
    if (active.request_id) feedbackAPI.markDismiss(active.request_id);
    setClosed(true);
  };
  return (
    <ModalShell onClose={dismiss}>
      <FeedbackCapture campaign={active.campaign} requestId={active.request_id} surface={surface} onDone={() => setClosed(true)} />
    </ModalShell>
  );
};

export const HelpShapeLomiItem: React.FC<{ className?: string }> = ({ className = 'dropdown-item' }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<ActiveResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await feedbackAPI.getActive('web_menu', 'menu');
      if (res.campaign) {
        setActive(res);
        if (res.request_id) feedbackAPI.markOpen(res.request_id);
      } else {
        setActive({ campaign: OPEN_ENDED }); // always let them speak
      }
    } catch {
      setActive({ campaign: OPEN_ENDED });
    }
    setLoading(false);
  };

  return (
    <>
      <button className={className} onClick={openModal}>
        <span style={{ width: 16, display: 'inline-block' }}>🍋</span>
        Help shape Lomi
      </button>
      {open && (
        <ModalShell onClose={() => setOpen(false)}>
          {loading || !active?.campaign ? (
            <div style={{ color: dark ? '#aab1ba' : '#64748b', fontSize: 14, padding: '8px 2px' }}>Loading…</div>
          ) : (
            <FeedbackCapture campaign={active.campaign} requestId={active.request_id} surface="web_menu" onDone={() => setOpen(false)} />
          )}
        </ModalShell>
      )}
    </>
  );
};

export default FeedbackAutoModal;
